import { useState, useCallback, useEffect } from 'react';
import FileService from '@/services/fileService';
import { showToast } from '@int-traderjoes/ui-components-library';
import { useLanguageContext } from '@/context/LanguageContext';
import { handleApiError } from '@/hooks/useApiErrorHandler';

// Types
export interface FileData {
  id: string;
  filename: string;
  fullpath?: string; // Full file path for download/display
  uploadedAt: string;
  size?: number;
  type: string;
  downloadUrl?: string; // Direct download URL
  previewUrl?: string; // Preview URL
}

export interface FileManagementState {
  [key: string]: FileData | null;
}

export interface FileUploadOptions {
  maxSize?: number;
  allowedTypes?: string[];
  allowedExtensions?: string[];
  customPath?: string;
}

export interface UseFileManagementProps {
  productId: string | number;
  variantId: string | number;
  documentFields: string[]; // Array of document field names like ['retailPackaging', 'outOfPackaging', 'shelfLifeStudy', etc.]
  onFileStateChange?: (fieldName: string, fileData: FileData | null) => void;
  uploadOptions?: FileUploadOptions;
  isGuest?: boolean;
}

export interface UseFileManagementReturn {
  files: FileManagementState;
  uploadingFiles: Set<string>;
  uploadFile: (fieldName: string, file: File, customPathOverride?: string) => Promise<FileData>;
  downloadFile: (fieldName: string) => Promise<void>;
  removeFile: (fieldName: string) => Promise<void>;
  getFileForPayload: (fieldName: string) => { id: string; filename: string } | null;
  getAllFilesForPayload: () => Record<string, { id: string; filename: string }>;
  isUploading: (fieldName: string) => boolean;
  hasFile: (fieldName: string) => boolean;
  getFileName: (fieldName: string) => string;
  clearAllFiles: () => void;
  populateExistingFiles: (existingFiles: Record<string, any>) => void;
}

const useFileManagement = ({
  productId,
  variantId,
  documentFields,
  onFileStateChange,
  uploadOptions = {},
  isGuest = false
}: UseFileManagementProps): UseFileManagementReturn => {
  
  const { languageText } = useLanguageContext();
  const toastMessages = languageText?.MESSAGES?.TOAST_MESSAGES;
  
  // State for tracking files
  const [files, setFiles] = useState<FileManagementState>(() => {
    const initialState: FileManagementState = {};
    documentFields.forEach(field => {
      initialState[field] = null;
    });
    return initialState;
  });

  // State for tracking upload progress
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());

  // Generate unique document ID based on product, variant, and document name
  const generateDocumentId = useCallback((fieldName: string): string => {
    return `${productId}_${variantId}_${fieldName}`;
  }, [productId, variantId]);

  // Upload file function
  const uploadFile = useCallback(async (fieldName: string, file: File, customPathOverride?: string): Promise<FileData> => {
    try {
      // console.log(`🔄 [useFileManagement] Starting upload for ${fieldName}:`, file.name);
      
      // Add to uploading set
      setUploadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.add(fieldName);
        return newSet;
      });

      // Default upload options
      const defaultOptions: FileUploadOptions = {
        maxSize: 40 * 1024 * 1024, // 40MB default
        allowedTypes: [
          'image/jpeg', 'image/png', 'image/gif', 'image/webp',
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ],
        allowedExtensions: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'doc', 'docx', 'xls', 'xlsx']
      };

      const finalOptions = { ...defaultOptions, ...uploadOptions };

      // Use customPathOverride if provided, otherwise use configured customPath, otherwise use default
      const customPath = customPathOverride || uploadOptions.customPath || generateDocumentId(fieldName);

      // Upload using FileService
      const result = await FileService.uploadFile(file, {
        ...finalOptions,
        customPath: customPath
      });

      if (result.success) {
        // Construct proper URLs using asset ID for API endpoints
        const downloadUrl = `/asset/${result.id}/download`;
        const previewUrl = `/asset/${result.id}/content`;
        
        const fileData: FileData = {
          id: result.id,
          filename: result.filename,
          fullpath: result.fullpath || customPath, // Use our custom path if API doesn't return fullpath
          uploadedAt: new Date().toISOString(),
          size: file.size,
          type: file.type,
          downloadUrl: downloadUrl, // Proper download URL using asset ID
          previewUrl: previewUrl // Proper preview URL using asset ID
        };
        
        console.log('🔍 [useFileManagement] File data created:', {
          fieldName,
          customPath,
          fullpath: fileData.fullpath,
          downloadUrl: fileData.downloadUrl,
          previewUrl: fileData.previewUrl
        });

        // Update files state
        setFiles(prev => ({
          ...prev,
          [fieldName]: fileData
        }));

        // Call callback if provided
        if (onFileStateChange) {
          onFileStateChange(fieldName, fileData);
        }

        // console.log(`✅ [useFileManagement] Upload successful for ${fieldName}:`, fileData);
        
        showToast({
          type: "success",
          message: toastMessages?.FILE_UPLOAD_SUCCESS?.replace('{fileName}', file.name)
        });

        // Return the file data for immediate use
        return fileData;

      } else {
        throw new Error(result.message || 'Upload failed');
      }

    } catch (error: any) {
      console.error(`❌ [useFileManagement] Upload failed for ${fieldName}:`, error);
      handleApiError(
        error,
        toastMessages?.FILE_UPLOAD_FAILED_ERROR?.replace('{fileName}', file.name) || `Failed to upload ${file.name}`
      );
      
      throw error;
    } finally {
      // Remove from uploading set
      setUploadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(fieldName);
        return newSet;
      });
    }
  }, [generateDocumentId, uploadOptions, onFileStateChange, toastMessages]);

  // Download file function
  const downloadFile = useCallback(async (fieldName: string): Promise<void> => {
    const fileData = files[fieldName];
    if (!fileData) {
      showToast({
        type: "error",
        message: toastMessages?.FILE_DOWNLOAD_NO_FILE_ERROR
      });
      return;
    }

    try {
      // console.log(`🔄 [useFileManagement] Starting download for ${fieldName}:`, fileData.filename);
      
      await FileService.downloadFile(fileData.id, fileData.filename, isGuest);
      
      // console.log(`✅ [useFileManagement] Download initiated for ${fieldName}`);
      
    } catch (error) {
      console.error(`❌ [useFileManagement] Download failed for ${fieldName}:`, error);
      handleApiError(
        error,
        toastMessages?.FILE_DOWNLOAD_FAILED_ERROR?.replace('{fileName}', fileData.filename) || `Failed to download ${fileData.filename}`
      );
    }
  }, [files, toastMessages]);

  // Remove file function
  // NOTE: This function now only removes files from frontend state.
  // The actual deletion is handled by the save API when null is sent for the field.
  const removeFile = useCallback(async (fieldName: string): Promise<void> => {
    const fileData = files[fieldName];
    
    if (!fileData) {
      return;
    }
    
    try {
      // Update local state - remove from frontend only
      // The save API will handle actual deletion by sending null for this field
      setFiles(prev => ({
        ...prev,
        [fieldName]: null
      }));

      // Call callback if provided
      if (onFileStateChange) {
        onFileStateChange(fieldName, null);
      }
      
      showToast({
        type: "success",
        message: toastMessages?.FILE_REMOVE_SUCCESS?.replace('{fileName}', fileData.filename)
      });

    } catch (error) {
      console.error(`❌ [useFileManagement] Failed to remove file for ${fieldName}:`, error);
      handleApiError(
        error,
        toastMessages?.FILE_REMOVE_FAILED_ERROR?.replace('{fileName}', fileData.filename) || `Failed to remove ${fileData.filename}`
      );
      
      throw error; // Re-throw to let caller handle
    }
  }, [files, onFileStateChange, toastMessages]);

  // Get file data for payload (only id and filename)
  const getFileForPayload = useCallback((fieldName: string): { id: string; filename: string } | null => {
    const fileData = files[fieldName];
    if (!fileData) return null;
    
    return {
      id: fileData.id,
      filename: fileData.filename
    };
  }, [files]);

  // Get all files for payload
  const getAllFilesForPayload = useCallback((): Record<string, { id: string; filename: string }> => {
    const payload: Record<string, { id: string; filename: string }> = {};
    
    Object.entries(files).forEach(([fieldName, fileData]) => {
      if (fileData) {
        payload[fieldName] = {
          id: fileData.id,
          filename: fileData.filename
        };
      }
    });
    
    return payload;
  }, [files]);

  // Check if file is uploading
  const isUploading = useCallback((fieldName: string): boolean => {
    return uploadingFiles.has(fieldName);
  }, [uploadingFiles]);

  // Check if field has file
  const hasFile = useCallback((fieldName: string): boolean => {
    return files[fieldName] !== null;
  }, [files]);

  // Get filename for display
  const getFileName = useCallback((fieldName: string): string => {
    const fileData = files[fieldName];
    return fileData ? fileData.filename : '';
  }, [files]);

  // Clear all files - removed documentFields dependency to prevent rerenders
  const clearAllFiles = useCallback(() => {
    setFiles(prev => {
      const clearedState: FileManagementState = {};
      // Use current documentFields from closure rather than dependency
      documentFields.forEach(field => {
        clearedState[field] = null;
      });
      return clearedState;
    });
  }, []); // Removed documentFields dependency

  // Populate existing files from API data
  const populateExistingFiles = useCallback((existingFiles: Record<string, any>) => {
    console.log('📁 [useFileManagement] Populating existing files:', existingFiles);
    
    const populatedState: FileManagementState = {};
    
    // Initialize all fields to null first
    documentFields.forEach(field => {
      populatedState[field] = null;
    });
    
    // Populate with existing file data
    Object.entries(existingFiles).forEach(([fieldName, fileData]) => {
      if (fileData && documentFields.includes(fieldName)) {
        populatedState[fieldName] = {
          id: fileData.fileId || fileData.id || '',
          filename: fileData.filename || '',
          fullpath: fileData.fullpath || '',
          uploadedAt: fileData.uploadedAt || new Date().toISOString(),
          size: fileData.size || 0,
          type: fileData.type || '',
          downloadUrl: fileData.downloadUrl || '',
          previewUrl: fileData.previewUrl || ''
        };
        
        console.log(`📁 [useFileManagement] Populated existing file for ${fieldName}:`, populatedState[fieldName]);
      }
    });
    
    setFiles(populatedState);
    console.log('✅ [useFileManagement] All existing files populated:', populatedState);
    
    // Call the callback to update FormContext AFTER setting files to prevent loops
    if (onFileStateChange) {
      Object.entries(populatedState).forEach(([fieldName, fileData]) => {
        if (fileData && documentFields.includes(fieldName)) {
          onFileStateChange(fieldName, fileData);
        }
      });
    }
  }, [documentFields]); // Removed onFileStateChange from dependencies to prevent recreation

  // Reset files when productId or variantId changes
  useEffect(() => {
    // console.log('🔄 [useFileManagement] Product/Variant changed, clearing files:', { productId, variantId });
    clearAllFiles();
  }, [productId, variantId, clearAllFiles]);

  return {
    files,
    uploadingFiles,
    uploadFile,
    downloadFile,
    removeFile,
    getFileForPayload,
    getAllFilesForPayload,
    isUploading,
    hasFile,
    getFileName,
    clearAllFiles,
    populateExistingFiles
  };
};

export default useFileManagement;

